import { get_token, get_user_details } from "@/router";
import Swal from "sweetalert2";

export default {
  emits: ["agenda-updated"],
  props: {
    event_id: {
      type: [Number, String],
      default: null,
    },
    event: {
      type: Object,
      default: null,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return ({
      current_event: null,
      current_event_id: null,
      selected_event_agenda: null,
      agenda_mode: "agenda_description",
      agenda: [],
      agenda_errors: {},
      upload_file: null,
      file_name: "",
      file_error: "",
      is_uploading: false,
      accepted_file_types: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.webp",
      upload_preview_url: "",
      upload_preview_type: "",
      upload_preview_is_object_url: false,
      preview_document_url: "",
      preview_document_type: "",
      loading_preview: false,
      user_details: null,
      can_create_agenda: false,
    });
  },
  computed: {
    has_existing_agenda() {
      return Array.isArray(this.current_event?.agenda) && this.current_event.agenda.length > 0;
    },
  },
  watch: {
    event: {
      immediate: true,
      deep: false,
      handler(next) {
        if (!this.inline) return;
        if (!next || typeof next !== "object") return;
        this.init_editor(next);
      },
    },
  },
  methods: {
    getToken() {
      return get_token();
    },
    get_user_id() {
      return localStorage.getItem("user_id") || "";
    },
    set_permissions(event) {
      this.user_details = get_user_details();
      const role = String(this.user_details?.user_role || "").toLowerCase();
      const is_admin = role === "admin";
      const event_owner_id = event?.user?.id;
      const is_owner = String(event_owner_id) === String(this.user_details?.user_id);
      this.can_create_agenda = is_admin || is_owner;
    },
    new_agenda_item: () => ({
      description: "",
      start_time: "",
      end_time: "",
      speaker: "",
    }),
    build_agenda_from_event(event) {
      const items = Array.isArray(event?.agenda) ? event.agenda : [];
      if (!items.length) return [this.new_agenda_item()];
      return items.map((item) => ({
        id: item?.id,
        description: item?.description || "",
        start_time: item?.start_time || "",
        end_time: item?.end_time || "",
        speaker: item?.speaker || "",
      }));
    },
    reset_create_form() {
      this.agenda_mode = "agenda_description";
      this.agenda = [this.new_agenda_item()];
      this.agenda_errors = {};
      this.upload_file = null;
      this.file_name = "";
      this.file_error = "";
      this.is_uploading = false;
      this.clear_upload_preview();
      this.current_event = null;
      this.current_event_id = null;
      this.can_create_agenda = false;
    },
    validate_event_access(event) {
        this.current_event_id = event?.id ?? this.event_id;
        this.set_permissions(event);
        return true;
    },

    // 2. New Helper: Handle File Preview Logic
    initialize_file_preview(event) {
        const agenda_files = this.get_agenda_files(event);
        const file_url = agenda_files?.[0]?.file?.file_url;
        if (file_url) {
            this.set_upload_preview_from_url(file_url);
        }
    },

    init_editor(event) {
      if (!this.validate_event_access(event)) return;
      this.current_event = event || null;
      this.agenda_mode = "agenda_description";
      this.agenda = this.build_agenda_from_event(event);
      this.reset_upload_state();
      this.initialize_file_preview(event);
    },

    // 3. The Main Method (Modal)
    open_create_agenda(event) {
      this.init_editor(event);
      if (this.inline) return;
      $("#create_event_agenda_modal").modal("show");
    },

      // 4. Dedicated Reset Method (Reduces noise in main function)
      reset_upload_state() {
          this.agenda_errors = {};
          this.upload_file = null;
          this.file_name = "";
          this.file_error = "";
          this.is_uploading = false;
          this.clear_upload_preview();
      },
    open_view_agenda(event) {
      this.reset_preview();
      this.selected_event_agenda = event;
      const agenda_files = this.get_agenda_files(event);
      if (agenda_files.length) {
        this.preview_document(agenda_files);
      }
      $("#event_agenda_modal").modal("show");
    },
    add_agenda_item() {
      this.agenda.push(this.new_agenda_item());
    },
    remove_agenda_item(index) {
      if (this.agenda.length <= 1) return;
      this.agenda.splice(index, 1);
    },
    validate_agenda_item(item, index) {
      let isItemValid = true;

      // Helper to log errors quickly
      const addError = (field, message) => {
        this.agenda_errors[`${field}${index}`] = message;
        isItemValid = false;
      };

      // Required Field Checks
      if (!item.description?.trim()) addError("agenda_description", "description is required.");
      if (!item.start_time?.trim()) addError("agenda_start_time", "start time is required.");
      if (!item.end_time?.trim()) addError("agenda_end_time", "end time is required.");

      // Time Comparison Logic
      if (item.start_time && item.end_time) {
        const startTime = new Date(`2000-01-01T${item.start_time}`).getTime();
        const endTime = new Date(`2000-01-01T${item.end_time}`).getTime();

        if (!isNaN(startTime) && !isNaN(endTime) && startTime >= endTime) {
          addError("agenda_end_time", "End time must be after start time");
        }
      }

      return isItemValid;
    },

    // 2. Main Validation Method (Now very simple)
    validate_agenda() {
      this.agenda_errors = {};
      if (!Array.isArray(this.agenda) || this.agenda.length === 0) {
        this.agenda_errors["agenda_empty"] = "Please add at least one agenda item.";
        return false;
      }

      const parse_agenda_time_ms = (time_string) => {
        if (!time_string || typeof time_string !== "string") return null;
        const time_ms = new Date(`2000-01-01T${time_string}`).getTime();
        return Number.isNaN(time_ms) ? null : time_ms;
      };

      const results = this.agenda.map((agenda_item, item_index) =>
        this.validate_agenda_item(agenda_item, item_index)
      );

      // Sequential time validation: next item's start must be after previous item's end.
      for (let item_index = 1; item_index < this.agenda.length; item_index += 1) {
        const previous_agenda_item = Reflect.get(this.agenda, item_index - 1);
        const current_agenda_item = Reflect.get(this.agenda, item_index);

        const previous_end_time_ms = parse_agenda_time_ms(previous_agenda_item?.end_time);
        const current_start_time_ms = parse_agenda_time_ms(current_agenda_item?.start_time);

        if (previous_end_time_ms === null || current_start_time_ms === null) continue;
        if (current_start_time_ms <= previous_end_time_ms) {
          Reflect.set(
            this.agenda_errors,
            `agenda_start_time${item_index}`,
            "Start time must be greater than the previous agenda item's end time."
          );

          Reflect.set(results, item_index, false);
        }
      }

      return results.every((valid) => valid === true);
    },
    is_allowed_file(file) {
      const name = String(file?.name || "").toLowerCase();
      const allowed = String(this.accepted_file_types || "")
        .split(",")
        .map((ext) => ext.trim().toLowerCase())
        .filter(Boolean);
      return allowed.some((ext) => name.endsWith(ext));
    },
    get_file_type_from_name(name) {
      const extension = String(name || "").split(".").pop().toLowerCase();
      if (extension === "pdf") return "pdf";
      if (["doc", "docx"].includes(extension)) return "word";
      if (["xls", "xlsx"].includes(extension)) return "excel";
      if (["ppt", "pptx"].includes(extension)) return "powerpoint";
      if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(extension)) return "image";
      if (extension === "txt") return "txt";
      return "unknown";
    },
    clear_upload_preview() {
      if (this.upload_preview_is_object_url && this.upload_preview_url) {
        URL.revokeObjectURL(this.upload_preview_url);
      }
      this.upload_preview_url = "";
      this.upload_preview_type = "";
      this.upload_preview_is_object_url = false;
    },
    set_upload_preview_from_url(file_url) {
      const file_type = this.get_file_type(file_url);
      if (file_type === "word" || file_type === "excel" || file_type === "powerpoint") {
        this.upload_preview_url =
          "https://view.officeapps.live.com/op/embed.aspx?src=" +
          encodeURIComponent(file_url);
      } else {
        this.upload_preview_url = file_url;
      }
      this.upload_preview_type = file_type;
      this.upload_preview_is_object_url = false;
    },
    set_upload_preview_from_file(file) {
      this.clear_upload_preview();
      const file_type = this.get_file_type_from_name(file?.name);
      this.upload_preview_type = file_type;
      if (file_type === "pdf" || file_type === "image") {
        this.upload_preview_url = URL.createObjectURL(file);
        this.upload_preview_is_object_url = true;
      }
    },
    handle_agenda_file_upload(event) {
      const file = event?.target?.files?.[0];
      if (!file) {
        this.upload_file = null;
        this.file_name = "";
        this.file_error = "";
        this.clear_upload_preview();
        return;
      }
      if (!this.is_allowed_file(file)) {
        this.upload_file = null;
        this.file_name = "";
        this.file_error = "Only PDF, Office, text, or image files are allowed.";
        this.clear_upload_preview();
        if (event?.target) event.target.value = "";
        return;
      }
      this.file_error = "";
      this.upload_file = file;
      this.file_name = file.name;
      this.set_upload_preview_from_file(file);
    },
    create_agenda() {
      if (this.agenda_mode === "agenda_description") {
        if (!this.validate_agenda()) return;
      } else {
        if (!this.upload_file) {
          this.file_error = "Please select an agenda file to upload.";
          return;
        }
        this.file_error = "";
      }

      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const formData = new FormData();
      formData.append("event_id", this.current_event_id);
      const agendaPayload =
        this.agenda_mode === "agenda_description"
          ? this.agenda
          : Array.isArray(this.current_event?.agenda)
          ? this.current_event.agenda
          : [];
      formData.append("agenda", JSON.stringify(agendaPayload));
      if (this.upload_file) {
        formData.append("file", this.upload_file);
      }

      this.is_uploading = true;
      $.ajax({
        type: "POST",
        url: `${process.env.VUE_APP_BACKEND_API_URL}/event_management/create_event_agenda/`,
        data: formData,
        processData: false,
        contentType: false,
        headers: {
          Authorization: `Token ${this.getToken()}`,
        },
        success: (response) => {
          if (response.status === "success") {
            Swal.fire({
              icon: "success",
              title: "Agenda Created",
              text: response.message || "Agenda saved successfully.",
              showConfirmButton: false,
              timer: 2000,
              position: 'bottom-end',
              toast: true
            }).then(() => {
              const nextAgenda = response.data;
              if(nextAgenda.agenda){
                this.current_event.agenda = nextAgenda.agenda;
              }
              if(nextAgenda.files){
                this.current_event.event_files = this.current_event.event_files.filter(
                  (file) => file.event_file_type?.name?.toLowerCase() !== "agenda"
                );
                this.current_event.event_files.unshift(...nextAgenda.files);
              }
              this.$emit("agenda-updated", {
                event_id: this.current_event_id,
                agenda: nextAgenda,
              });

               if (!this.inline) {
                 $("#create_event_agenda_modal").modal("hide");
                 this.reset_create_form();
               } else {
                 this.reset_upload_state();
                 this.agenda = this.build_agenda_from_event(this.current_event);
               }
             });
           }
         },
        error: (response) => {
          Swal.close();
          const response_data = response.responseJSON || {};
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response_data.message || "An error occurred while creating the agenda.",
            showConfirmButton: false,
            timer: 3000,
            position: 'bottom-end',
            toast: true
          });
        },
        complete: () => {
          this.is_uploading = false;
        },
      });
    },
    get_agenda_files(event) {
      return (event?.event_files || []).filter((file) => {
        const name = String(file?.event_file_type?.name || "").toLowerCase();
        return name === "agenda" || name.includes("agenda");
      });
    },
    preview_document(fileObj) {
      if (fileObj && fileObj[0]?.file?.file_url) {
        const file_url = fileObj[0].file.file_url;
        const file_type = this.get_file_type(file_url);

        this.loading_preview = true;

        if (file_type === "word" || file_type === "excel" || file_type === "powerpoint") {
          this.preview_document_url =
            "https://view.officeapps.live.com/op/embed.aspx?src=" +
            encodeURIComponent(file_url);
        } else {
          this.preview_document_url = file_url;
        }
        this.preview_document_type = file_type;
      } else {
        console.error("File object or file URL is missing");
      }
    },
    reset_preview() {
      this.preview_document_url = "";
      this.preview_document_type = "";
      this.loading_preview = false;
      this.selected_event_agenda = null;
    },
    get_file_type(url) {
      if (typeof url !== "string") return "unknown";
      try {
        const parsed_url = new URL(url);
        const pathname = parsed_url.pathname;
        const filename = pathname.split("/").pop();
        const extension = String(filename || "").split(".").pop().toLowerCase();

        if (extension === "pdf") return "pdf";
        if (["doc", "docx"].includes(extension)) return "word";
        if (["xls", "xlsx"].includes(extension)) return "excel";
        if (["ppt", "pptx"].includes(extension)) return "powerpoint";
        if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(extension)) return "image";

        return "unknown";
      } catch (error) {
        console.error("Invalid URL in getFileType:", error);
        return "unknown";
      }
    },
    exportPDF() {
      if (!this.selected_event_agenda?.agenda?.length) return;
      const doc = new window.jspdf.jsPDF();
      doc.text("Meeting Agenda", 14, 15);

      const tableRows = this.selected_event_agenda.agenda.map((item) => [
        item.description,
        item.start_time,
        item.end_time,
        item.speaker,
      ]);

      doc.autoTable({
        startY: 20,
        head: [["Topic", "Start", "End", "Speaker"]],
        body: tableRows,
        styles: { fontSize: 9 },
        headStyles: { fillBlue: [41, 128, 185] },
      });

      doc.save("agenda.pdf");
    },
  },
};
